// The Connections widget (Connections review, 22 Oct): every MCP server and key the user's agents and board use, on
// every computer, as one line ("41 connections · 4 need you"), red only when something fails, amber while something
// waits for a sign-in, calm otherwise. A click opens the full view (src/panels/ConnectionsView.tsx). The owner's
// alone: a shared view or a coworker sees that it is private, and the board sends them none of it.

import { useEffect, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { Cable, Lock } from "lucide-react";
import type { ComputerConnections } from "../../shared/types";
import { useSize } from "../charts/charts";
import { cx } from "../lib/util";
import { loadAgentConnections, useAgentConnections } from "../state/agentConnections";
import { loadGoogle, useGoogle } from "../state/google";
import { loadSearchConsole, useSearchConsole } from "../state/searchConsole";
import { set, useStore } from "../state/store";
import { connectionsReport, summaryLine, type ConnReport } from "./connectionsReport";
import type { WP } from "./data";
import { quarter } from "./layout";
import { connectionsFit } from "./plans";
import { useTick } from "./tick";
import { useSample } from "./WidgetTile";

/** Google's two statuses, read once a page: a read that failed is said, never taken as not connected. */
const googleRead = { google: null as Promise<void> | null, searchConsole: null as Promise<void> | null };
function useGoogleStatuses(skip: boolean) {
  const [failed, setFailed] = useState<string[]>([]);
  useEffect(() => {
    if (skip) return;
    googleRead.google ??= loadGoogle().then(
      () => {},
      () => setFailed((f) => [...f, "Google Analytics"]),
    );
    googleRead.searchConsole ??= loadSearchConsole().then(
      () => {},
      () => setFailed((f) => [...f, "Search Console"]),
    );
  }, [skip]);
  return failed;
}

export interface ConnView {
  report: ConnReport;
  /** everything the count is made of has come back */
  ready: boolean;
  /** what could not be read, said in words beside the count */
  problems: string[];
  /** the computers whose servers the count does not hold yet: their runner is updating, or they are off and were never read */
  unread: string[];
}

/** Everything that connects, as one report: the board's own from the store, each computer's as it last read. */
export function useConnReport(sample?: ComputerConnections[] | null): ConnView {
  useTick(60_000);
  const connections = useStore(useShallow((s) => Object.values(s.connections)));
  const services = useStore(useShallow((s) => Object.values(s.services)));
  const keys = useStore((s) => s.keys);
  const google = useGoogle((s) => s.status);
  const searchConsole = useSearchConsole((s) => s.status);
  const { computers: read, error } = useAgentConnections();
  // whether each computer is on, and whether it is still linked, as the board says it now: a computer that goes off or
  // is unlinked says so here at once, with what it last read
  const live = useStore((s) => s.runner?.computers ?? null);
  const computers = useMemo(
    () =>
      read && live
        ? read
            .filter((c) => live.some((x) => x.id === c.id))
            .map((c) => {
              const l = live.find((x) => x.id === c.id)!;
              return { ...c, online: l.online, looking: l.online ? c.looking : null };
            })
        : read,
    [read, live],
  );
  const failed = useGoogleStatuses(!!sample);
  useEffect(() => {
    if (!sample) void loadAgentConnections();
  }, [sample]);
  const now = Math.floor(Date.now() / 60_000) * 60_000;
  const report = useMemo(
    () =>
      sample
        ? connectionsReport({ now, connections: [], services: [], keys: [], google: null, searchConsole: null, computers: sample })
        : connectionsReport({ now, connections, services, keys, google: failed.includes("Google Analytics") ? null : google, searchConsole: failed.includes("Search Console") ? null : searchConsole, computers }),
    [sample, now, connections, services, keys, google, searchConsole, computers, failed],
  );
  if (sample) return { report, ready: true, problems: [], unread: [] };
  const googleDone = (!!google || failed.includes("Google Analytics")) && (!!searchConsole || failed.includes("Search Console"));
  const problems = [...(error ? [`Your computers could not be read: ${error}`] : []), ...failed.map((f) => `${f} could not be read.`)];
  // a computer on now whose runner reads its servers and has not sent them yet: no count until it has (a minute at most)
  const first = (computers ?? []).some((c) => c.online && c.reads && !c.at);
  // one whose servers the count cannot hold yet (its runner is updating, or it is off and never read): named beside it
  const unread = (computers ?? []).filter((c) => !c.at && !(c.online && c.reads)).map((c) => c.host);
  return { report, ready: (computers !== null || !!error) && googleDone && !first, problems, unread };
}

export const openConnections = () => set({ panel: "connections", panelArg: null });

/** What needs the user, by kind, in the words the tile and the view share. */
export function needParts(r: ConnReport): string[] {
  const n = (kind: string) => r.flags.filter((f) => f.kind === kind).length;
  return [
    n("failed") && `${n("failed")} failing`,
    n("signin") && `${n("signin")} to sign in`,
    n("doubled") && `${n("doubled")} doubled up`,
    n("unused") && `${n("unused")} unused`,
  ].filter((x): x is string => !!x);
}

export function ConnectionsW({ readOnly }: WP) {
  const sample = useSample();
  // a shared link, or a coworker's page, even one who can edit the board: the owner's alone, never the viewer's own
  const guest = useStore((s) => !!s.guest);
  const shared = (readOnly || guest) && !sample;
  const { report, ready, problems, unread } = useConnReport(sample ? (sample.connections ?? []) : shared ? [] : null);
  const [ref, size] = useSize<HTMLButtonElement>();
  if (shared)
    return (
      <div className="pw pw-empty">
        <Lock size={16} />
        <b>Private</b>
        <span>The owner's connections are only for the owner.</span>
      </div>
    );
  const fit = connectionsFit(size);
  const parts = needParts(report);
  const offline = report.places.filter((p) => p.kind === "computer" && !p.online).map((p) => p.name);
  const tone = !ready ? "calm" : report.tone;
  const said = !ready ? "Reading your connections" : unread.length ? `${summaryLine(report)} · ${unread.length} ${unread.length === 1 ? "computer" : "computers"} to come` : summaryLine(report);
  return (
    <button
      ref={ref}
      className={cx("pw cnw", `is-${fit.id}`, `tone-${tone}`, !ready && "is-reading")}
      style={size.w > 0 ? { fontSize: quarter(fit.px) } : undefined}
      onClick={() => !sample && openConnections()}
      aria-label={`${said}. Open every connection`}
      data-interactive
    >
      {fit.id === "full" ? (
        <>
          <span className="cnw-count">
            {/* nothing counted before everything it counts has come back */}
            <b>{ready ? report.total : "…"}</b>
            <span>{report.total === 1 && ready ? "connection" : "connections"}</span>
          </span>
          <span className="cnw-need">
            <span className="dot" aria-hidden />
            {!ready ? "Reading your computers…" : report.needYou ? `${report.needYou} need${report.needYou === 1 ? "s" : ""} you` : "Nothing needs you"}
          </span>
          <span className="cnw-parts">{problems[0] ?? (unread.length ? `Not counted yet: ${unread.join(" and ")}, until ${unread.length === 1 ? "its" : "their"} runner reads ${unread.length === 1 ? "its" : "their"} servers` : parts.length ? parts.join(" · ") : offline.length ? `${offline.join(" and ")} ${offline.length === 1 ? "is" : "are"} off, as last read` : report.places.length > 1 ? "Every one works" : "Link a computer to see your agents' servers")}</span>
        </>
      ) : (
        <span className="cnw-line">
          <span className="dot" aria-hidden />
          <Cable size={14} aria-hidden />
          {said}
        </span>
      )}
    </button>
  );
}
